import { AfterViewInit, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { ModalV2Component } from "../../../../../shared/modal-v2/modal-v2.component";
import { InputSelectComponent } from "../../../../../shared/input-select/input-select.component";
import { Cultures } from "../../../../../core/constants/cultures";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { DiaryFormElementMessage } from "../../../../../core/models/trial-diary-form/diary-form-element-message.model";
import { DiaryFormElement } from "../../../../../core/models/trial-diary-form/diary-form-element.model";
import { SelectOption } from "../../../../../core/models/select-option.model";
import { DiaryFormElementType } from "../../../../../core/constants/diary-form-element-type.enum";
import {
  noWhitespaceValidator,
  requiredNoWhitespaceValidator
} from "../../../../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-element-message-modal',
  templateUrl: './element-message-modal.component.html',
  styleUrl: './element-message-modal.component.scss'
})
export class ElementMessageModalComponent implements AfterViewInit {
  @ViewChild('modal') modal: ModalV2Component;
  @ViewChild('cultureSelect') cultureSelect: InputSelectComponent;
  @ViewChild('choiceSelect') choiceSelect: InputSelectComponent;

  @Output() added = new EventEmitter<{elementIndex: number, message: DiaryFormElementMessage}>();
  @Output() updated = new EventEmitter<{elementIndex: number, message: DiaryFormElementMessage}>();

  protected elementIndex: number = 0;
  protected element: DiaryFormElement;
  protected message: DiaryFormElementMessage;
  protected availableCultures: { value: string, text: string }[] = [];
  protected filteredCultures: { value: string, text: string }[] = [];
  protected editing: boolean = false;

  protected readonly DiaryFormElementType = DiaryFormElementType;

  protected operatorOptions: SelectOption[] = [
    new SelectOption('0', 'AND'),
    new SelectOption('1', 'OR')
  ];

  protected styleOptions: SelectOption[] = [
    new SelectOption('danger', 'Danger (red)'),
    new SelectOption('success', 'Success (green)'),
    new SelectOption('info', 'Information')
  ];

  protected choiceOptions: SelectOption[] = [];

  protected cultureForms: FormGroup[] = [];
  form: FormGroup = new FormGroup({
    selectedCulture: new FormControl<string>('en'),
    active: new FormControl<boolean>(false),
    showInSummary: new FormControl<boolean>(false),
    style: new FormControl<string>('info'),
    operator: new FormControl<string>('0'),
    patientChoices: new FormControl<string[]>([]),
    patientChoice: new FormControl<string>('')
  });

  ngAfterViewInit() {
    this.initCultures();
  }

  initCultures() {
    Cultures.all().forEach(culture => {
      this.availableCultures.push({ value: culture.culture, text: culture.name });
    });
    this.filteredCultures = this.availableCultures;
  }

  onSelectLanguage(language: string) {
    const culture = this.availableCultures.find(t => t.text === language);
    if (culture) {
      this.form.patchValue({selectedCulture: culture.value});
    }
  }

  onCultureFiltered(value: string) {
    // Trim the input value and convert it to lowercase for case-insensitive filtering
    const filterValue = value.trim().toLowerCase();

    this.filteredCultures = this.availableCultures.filter(x =>
      x.text.toLowerCase().includes(filterValue)
    );
  }

  onPatientChoiceChanged(value: string) {
    this.form.patchValue({patientChoice: value});
  }

  resetForm() {
    this.form.patchValue({
      selectedCulture: 'en',
      active: false,
      showInSummary: false,
      style: 'info',
      operator: '0',
      patientChoice: '',
      patientChoices: []
    });
  }

  private updateChoiceValidators(elementType: DiaryFormElementType): void {
    const patientChoices = this.form.get('patientChoices');
    const patientChoice = this.form.get('patientChoice');
    const operator = this.form.get('operator');

    if (elementType === DiaryFormElementType.Checkbox) {
      patientChoices.setValidators([Validators.required]);
      patientChoice.clearValidators();
      operator.setValidators([Validators.required]);
    } else {
      patientChoices.clearValidators();
      patientChoice.setValidators([Validators.required]);
      operator.clearValidators();
    }

    patientChoices.updateValueAndValidity();
    patientChoice.updateValueAndValidity();
    operator.updateValueAndValidity();
  }

  add(elementIndex: number, element: DiaryFormElement) {
    this.elementIndex = elementIndex;
    this.element = element;
    this.editing = false;
    this.buildChoiceOptions();
    this.updateChoiceValidators(element.elementType);

    this.resetForm();
    this.updateChoiceValidators(element.elementType);
    this.message = new DiaryFormElementMessage({
      elementId: element.id,
      style: 'info',
      operator: 0,
      patientChoice: '',
      translations: []
    });

    this.buildCultureForms();

    this.modal.show();
  }

  edit(elementIndex: number, element: DiaryFormElement, message: DiaryFormElementMessage) {
    this.elementIndex = elementIndex;
    this.element = element;
    this.message = message;
    this.editing = true;
    this.buildChoiceOptions();
    this.updateChoiceValidators(element.elementType);

    this.form.patchValue({
      selectedCulture: 'en',
      active: message.active,
      showInSummary: message.showInSummary,
      operator: message.operator.toString(),
      style: message.style,
      patientChoice: message.patientChoice,
      patientChoices: message.patientChoice.split(',').map(choice => choice.trim())
    });

    this.buildCultureForms();

    this.modal.show();
  }

  buildChoiceOptions() {
    this.choiceOptions = this.element.options.map(option => new SelectOption(option.id, option.text));
  }

  buildCultureForms() {
    // Build culture forms
    this.cultureForms = [];
    this.availableCultures.forEach(culture => {
      const translation = this.message.translations.find(t => t.culture === culture.value);
      const textValidators = culture.value === 'en' ? [requiredNoWhitespaceValidator(), Validators.maxLength(1000)] : [noWhitespaceValidator(), Validators.maxLength(1000)];
      this.cultureForms.push(new FormGroup({
        diaryFormElementMessageId: new FormControl<string>(this.message.id),
        culture: new FormControl<string>(culture.value),
        text: new FormControl<string>(translation?.text, textValidators)
      }));
    });
  }

  hide() {
    this.modal.hide();
  }

  isSelectedCulture(culture: string) {
    return this.form.get('selectedCulture')?.value === culture;
  }

  get selectedCultureName(): string {
    const cultureCode = this.form.get('selectedCulture')?.value;
    return this.availableCultures.find(c => c.value === cultureCode)?.text ?? '';
  }

  get visibilityMessage(): string {
    if (this.element.elementType === DiaryFormElementType.Checkbox) {
      const selectedIds: string[] = this.form.get('patientChoices')?.value || [];
      if (selectedIds.length === 0) {
        return '';
      }

      const selectedTexts = selectedIds
        .map(id => this.choiceOptions.find(opt => opt.value === id)?.text)
        .filter(text => text != null);

      if (selectedTexts.length === 0) {
        return '';
      }

      const operatorValue = this.form.get('operator')?.value;
      const operatorText = operatorValue === '1' ? 'OR' : 'AND';

      return selectedTexts.join(` <strong>${operatorText}</strong> `);
    }

    const selectedId: string = this.form.get('patientChoice')?.value || '';
    if (!selectedId) {
      return '';
    }

    return this.choiceOptions.find(opt => opt.value === selectedId)?.text || '';
  }

  isFormValid(): boolean {
    return this.form.valid && this.cultureForms.every(cf => cf.valid);
  }

  onHandleApply() {
    if (!this.isFormValid()) {
      return;
    }

    const form = this.form;

    // Add logic to apply changes to message object and let the parent component know so it can handle it
    this.message.active = form.get('active').value;
    this.message.showInSummary = form.get('showInSummary').value;
    this.message.operator = +form.get('operator').value;
    if (this.element.elementType === DiaryFormElementType.Checkbox) {
      this.message.patientChoice = form.get('patientChoices').value.join(',');
    } else {
      this.message.patientChoice = form.get('patientChoice').value;
    }
    this.message.style = form.get('style').value;

    this.cultureForms.forEach(cf => {
      const translation = this.message.translations.find(t => t.culture == cf.get('culture').value);
      if (translation) {
        translation.text = cf.get('text').value;
      } else {
        this.message.translations.push({ diaryFormElementMessageId: this.message.id, culture: cf.get('culture').value, text: cf.get('text').value });
      }
    });

    if (this.editing) {
      this.updated.emit({elementIndex: this.elementIndex, message: this.message});
    } else {
      this.added.emit({elementIndex: this.elementIndex, message: this.message});
    }

    this.resetForm();
    this.hide();
  }
}
